import { renderInAppBrand } from '../../components/in-app-brand.mjs';
import {
  escapeAttribute,
  escapeHtml,
  formatListingPrice,
} from '../../utils/rendering.mjs';
import {
  buildImageFallbackHandler,
  sanitizeListingImageUrl,
} from '../../utils/image-fallbacks.mjs';
import { renderRatingStars } from '../../utils/rating-stars.mjs';
import {
  getFashionAttributes,
  getFashionItemTypeLabel,
} from '../../utils/fashion-attributes.mjs';
import { buildWhatsAppChatLink } from '../../utils/whatsapp-link.mjs';

const categoryLabels = {
  agriculture: 'Agriculture',
  beauty: 'Beauté',
  construction: 'Bricolage / Construction',
  education: 'École / Université',
  emploi: 'Emplois',
  electronics: 'Électronique',
  fashion: 'Mode',
  food: 'Alimentation',
  health: 'Santé',
  home_garden: 'Maison',
  music: 'Musique',
  phones_tablets: 'Téléphones & Tablettes',
  real_estate: 'Immobilier',
  services: 'Services',
  sports_leisure: 'Sports et loisirs',
  vehicles: 'Véhicules',
};

function clampSelectedImageIndex(index, images) {
  const parsedIndex = Number(index);

  if (!Number.isInteger(parsedIndex) || parsedIndex < 0) {
    return 0;
  }

  return Math.min(parsedIndex, Math.max(images.length - 1, 0));
}

function buildBuyerMessage(detail) {
  return `Bonjour, je suis intéressé par ${detail.title} sur Zwibba.`;
}

function buildLifecycleActionButton({
  action,
  detail,
  label,
  tone = 'primary',
} = {}) {
  const className =
    tone === 'secondary'
      ? 'app-flow__button app-flow__button--secondary'
      : 'app-flow__button';

  return `
    <button
      class="${className}"
      type="button"
      data-action="listing-lifecycle"
      data-lifecycle-action="${escapeAttribute(action)}"
      data-listing-id="${escapeAttribute(detail.id)}"
      data-listing-slug="${escapeAttribute(detail.slug)}"
    >
      ${escapeHtml(label)}
    </button>
  `;
}

function buildEditListingButton(detail) {
  return `
    <button
      class="app-flow__button"
      type="button"
      data-action="edit-listing"
      data-listing-slug="${escapeAttribute(detail.slug)}"
    >
      Modifier
    </button>
  `;
}

function buildShareButton(detail, { compact = false } = {}) {
  if (!detail.slug) {
    return '';
  }

  const shareUrl = `/annonce/${detail.slug}/`;
  const className = compact
    ? 'app-flow__button app-flow__button--icon'
    : 'app-flow__button app-flow__button--share';

  return `
    <button
      class="${escapeAttribute(className)}"
      type="button"
      data-action="open-share-menu"
      data-share-slug="${escapeAttribute(detail.slug)}"
      data-share-title="${escapeAttribute(detail.title)}"
      data-share-image-url="${escapeAttribute(resolveGalleryImages(detail)[0] || '')}"
      data-share-url="${escapeAttribute(shareUrl)}"
      ${detail.shareImageUrl ? `data-branded-image-url="${escapeAttribute(detail.shareImageUrl)}"` : ''}
      ${detail.storyImageUrl ? `data-story-image-url="${escapeAttribute(detail.storyImageUrl)}"` : ''}
      ${compact ? `aria-label="${escapeAttribute('Partager cette annonce')}"` : ''}
    >
      ${compact ? '<span aria-hidden="true">↗</span>' : 'Partager'}
    </button>
  `;
}

function buildActionMarkup(action, detail, contactBusy = false) {
  switch (action) {
    case 'message':
      return `
        <button
          class="app-flow__button app-flow__button--secondary"
          type="button"
          data-action="start-thread" ${contactBusy ? 'disabled aria-busy="true"' : ''}
          data-listing-id="${escapeAttribute(detail.id)}"
          data-listing-slug="${escapeAttribute(detail.slug)}"
        >
          <span aria-hidden="true">✉</span> ${contactBusy ? 'Ouverture…' : 'Envoyer un message'}
        </button>
      `;
    case 'whatsapp': {
      const chatLink = buildWhatsAppChatLink(
        detail.contactPhoneNumber,
        `Bonjour, votre annonce « ${detail.title} » sur Zwibba m'intéresse.`,
      );

      if (!chatLink) {
        return '';
      }

      return `
        <a
          class="app-flow__button"
          href="${escapeAttribute(chatLink)}"
          target="_blank"
          rel="noreferrer"
        >
          <span aria-hidden="true">↗</span> WhatsApp
        </a>
      `;
    }
    case 'call':
      if (detail.contactPhoneNumber) {
        return `
          <a
            class="app-flow__button app-flow__button--secondary"
            href="${escapeAttribute(`tel:${detail.contactPhoneNumber}`)}"
          >
            <span aria-hidden="true">☎</span> Appeler
          </a>
        `;
      }

      return `
        <button class="app-flow__button app-flow__button--secondary" type="button" disabled>
          Appeler
        </button>
      `;
    default:
      return '';
  }
}

function renderOwnerLifecycleCard(detail) {
  const actions = [];

  if (detail.editDraft) {
    actions.push(buildEditListingButton(detail));

    if (detail.slug) {
      actions.push(buildShareButton(detail, { compact: false }));
    }
  }

  if (detail.canPause) {
    actions.push(
      buildLifecycleActionButton({
        action: 'pause',
        detail,
        label: 'Mettre en pause',
      }),
    );
  }

  if (detail.canResume) {
    actions.push(
      buildLifecycleActionButton({
        action: 'resume',
        detail,
        label: 'Remettre en ligne',
      }),
    );
  }

  if (detail.canMarkSold) {
    actions.push(
      buildLifecycleActionButton({
        action: 'mark_sold',
        detail,
        label: 'Marquer comme vendue',
      }),
    );
  }

  if (detail.canRelist) {
    actions.push(
      buildLifecycleActionButton({
        action: 'relist',
        detail,
        label: 'Remettre en vente',
      }),
    );
  }

  if (detail.canRestore) {
    actions.push(
      buildLifecycleActionButton({
        action: 'restore',
        detail,
        label: 'Restaurer',
      }),
    );
  }

  if (detail.canDelete) {
    actions.push(
      buildLifecycleActionButton({
        action: 'delete',
        detail,
        label: 'Supprimer l’annonce',
        tone: 'secondary',
      }),
    );
  }

  return `
    <div class="app-auth__card app-detail__owner-card">
      <strong>Gérer mon annonce</strong>
      <p>Statut actuel: ${escapeHtml(detail.lifecycleStatusLabel || 'Active')}</p>
      ${
        detail.soldChannel
          ? `<span class="app-detail__owner-meta">${escapeHtml(detail.soldChannel)}</span>`
          : ''
      }
      ${
        detail.deletedReason
          ? `<span class="app-detail__owner-meta">${escapeHtml(detail.deletedReason)}</span>`
          : ''
      }
      ${
        detail.restoreUntil
          ? `<span class="app-detail__owner-meta">Restaurable jusqu’au ${escapeHtml(new Date(detail.restoreUntil).toLocaleDateString('fr-FR'))}</span>`
          : ''
      }
      ${
        actions.length
          ? `<div class="app-flow__actions app-flow__actions--stacked app-flow__actions--row">${actions.join('')}</div>`
          : ''
      }
    </div>
  `;
}

function resolveCategoryLabel(detail) {
  return detail.categoryLabel || categoryLabels[detail.categoryId] || 'Annonce';
}

function renderAttributeDetails(detail) {
  if (detail.categoryId !== 'fashion') {
    return '';
  }

  const fashionAttributes = getFashionAttributes(detail.attributesJson);
  const itemTypeLabel = getFashionItemTypeLabel(fashionAttributes.itemType);

  if (!itemTypeLabel && !fashionAttributes.size) {
    return '';
  }

  return `
    <div class="app-auth__card">
      <strong>Détails</strong>
      ${
        itemTypeLabel
          ? `<p><strong>Type d'article :</strong> ${escapeHtml(itemTypeLabel)}</p>`
          : ''
      }
      ${
        fashionAttributes.size
          ? `<p><strong>Taille :</strong> <span>${escapeHtml(fashionAttributes.size)}</span></p>`
          : ''
      }
    </div>
  `;
}

function resolveGalleryImages(detail) {
  const rawImages = Array.isArray(detail.images) && detail.images.length
    ? detail.images
    : detail.primaryImageUrl
      ? [detail.primaryImageUrl]
      : [];

  return rawImages
    .map((imageUrl) =>
      sanitizeListingImageUrl(imageUrl, {
        categoryId: detail.categoryId,
        categoryLabel: detail.categoryLabel,
      }),
    )
    .filter(Boolean);
}

function renderDetailMedia(detail, selectedImageIndex = 0) {
  const images = resolveGalleryImages(detail);
  const activeIndex = clampSelectedImageIndex(selectedImageIndex, images);
  const imageUrl = images[activeIndex];

  if (imageUrl) {
    const imageFallback = buildImageFallbackHandler({
      categoryId: detail.categoryId,
      categoryLabel: detail.categoryLabel,
    });

    return `
      <div class="app-detail__gallery">
        <div class="app-detail__media">
          <img
            class="app-detail__image"
            src="${escapeAttribute(imageUrl)}"
            alt="${escapeAttribute(detail.title)}"
            loading="eager"
            onerror="${escapeAttribute(imageFallback)}"
          />
        </div>
        ${
          images.length > 1
            ? `
              <div class="app-detail__thumbstrip" role="list" aria-label="Autres photos">
                ${images
                  .map(
                    (thumbnailUrl, index) => `
                      <button
                        class="app-detail__thumbnail${index === activeIndex ? ' is-active' : ''}"
                        type="button"
                        role="listitem"
                        aria-pressed="${index === activeIndex ? 'true' : 'false'}"
                        data-action="select-listing-image"
                        data-image-index="${escapeAttribute(String(index))}"
                      >
                        <img
                          class="app-detail__thumbnail-image"
                          src="${escapeAttribute(thumbnailUrl)}"
                          alt="${escapeAttribute(`${detail.title} photo ${index + 1}`)}"
                          loading="lazy"
                          onerror="${escapeAttribute(imageFallback)}"
                        />
                      </button>
                    `,
                  )
                  .join('')}
              </div>
            `
            : ''
        }
      </div>
    `;
  }

  return `
    <div class="app-detail__media app-detail__media--placeholder" aria-hidden="true">
      <span>Photo à venir</span>
    </div>
  `;
}

function renderSafetyCard(detail) {
  if (!Array.isArray(detail.safetyTips) || detail.safetyTips.length === 0) {
    return '';
  }

  return `
    <section class="app-detail__safety-card" aria-label="Conseils de sécurité">
      <div class="app-detail__safety-head">
        <span class="app-detail__safety-icon" aria-hidden="true">!</span>
        <div class="app-detail__safety-copy">
          <strong>Conseils de sécurité</strong>
          <span>Attention</span>
        </div>
      </div>
      <ul class="app-detail__safety-list">
        ${detail.safetyTips
          .map(
            (tip) => `
              <li>${escapeHtml(tip)}</li>
            `,
          )
          .join('')}
      </ul>
    </section>
  `;
}

function renderSellerBlock(seller = {}) {
  const sellerName = seller.name || 'Vendeur Zwibba';
  const sellerRole = seller.role || 'Vendeur';
  const memberDate = seller.memberSince && new Date(seller.memberSince);
  const memberSince = memberDate && Number.isFinite(memberDate.getTime()) ? memberDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }) : '';
  const sellerId = String(seller.sellerId || '').trim();
  const sellerContent = `
    <strong>${escapeHtml(sellerName)}</strong>
    <span>${escapeHtml(sellerRole)}</span>
    ${memberSince ? `<span>Membre depuis ${escapeHtml(memberSince)}</span>` : ''}
    ${renderRatingStars({
      average: seller.ratingAverage,
      count: seller.ratingCount,
    })}
  `;

  if (!sellerId) {
    return `
      <div class="app-publish__status is-verified">
        ${sellerContent}
      </div>
    `;
  }

  return `
    <a class="app-publish__status is-verified app-detail__seller-link" href="#seller/${escapeAttribute(sellerId)}">
      ${sellerContent}
    </a>
  `;
}

function renderReviewForm(detail) {
  if (detail.viewerRole === 'owner') {
    return '';
  }

  if (!detail.canReview) return '<p class="app-auth__card">Envoyez un message dans Zwibba avant de laisser un avis.</p>';

  return `
    <form class="app-auth__card app-detail__review-form" data-action="submit-review">
      <input type="hidden" name="slug" value="${escapeAttribute(detail.slug)}" />
      <strong>Votre avis</strong>
      <fieldset class="app-detail__rating-input">
        <legend>Votre note</legend>
        ${[1,2,3,4,5].map(rating => `<label><input type="radio" name="rating" value="${rating}" required aria-label="${rating} étoile${rating > 1 ? 's' : ''}" /><span aria-hidden="true">★</span></label>`).join('')}
      </fieldset>
      <label class="app-detail__review-field">
        <span>Commentaire</span>
        <textarea name="comment" rows="3" maxlength="280" placeholder="Optionnel"></textarea>
      </label>
      <button class="app-flow__button" type="submit">Envoyer l’avis</button>
      <p class="app-detail__review-message" data-review-message></p>
    </form>
  `;
}

export function renderListingDetailScreen({
  detail = null,
  errorMessage = '',
  contactBusy = false,
  contactError = '',
  selectedImageIndex = 0,
  state = 'loading',
} = {}) {
  if (state === 'loading') {
    return `
      <section class="app-flow app-flow--detail">
        <header class="app-flow__header">
          <div class="app-flow__meta">
            ${renderInAppBrand({ compact: true })}
            <a class="app-flow__back" href="#buy">Retour aux annonces</a>
          </div>
          <div>
            <p class="app-flow__eyebrow">Acheter</p>
            <h2 class="app-flow__title">Chargement de l'annonce</h2>
          </div>
        </header>
      </section>
    `;
  }

  if (state === 'error' || !detail) {
    return `
      <section class="app-flow app-flow--detail">
        <header class="app-flow__header">
          <div class="app-flow__meta">
            ${renderInAppBrand({ compact: true })}
            <a class="app-flow__back" href="#buy">Retour aux annonces</a>
          </div>
          <div>
            <p class="app-flow__eyebrow">Acheter</p>
            <h2 class="app-flow__title">Annonce indisponible</h2>
          </div>
        </header>

        <div class="app-review__error-summary" data-review-errors>
          <strong>Impossible d'ouvrir cette annonce</strong>
          <ul class="app-review__errors">
            <li>${escapeHtml(errorMessage || 'Annonce introuvable.')}</li>
          </ul>
        </div>
      </section>
    `;
  }

  const categoryLabel = resolveCategoryLabel(detail);

  return `
    <section class="app-flow app-flow--detail">
      <header class="app-flow__header">
        <div class="app-flow__meta">
          ${renderInAppBrand({ compact: true, countryCode: detail.countryCode })}
          ${buildShareButton(detail, { compact: true })}
          <a class="app-flow__back" href="#buy">Retour aux annonces</a>
        </div>
        <div>
          <p class="app-flow__eyebrow">Acheter</p>
          <h2 class="app-flow__title">${escapeHtml(detail.title)}</h2>
        </div>
      </header>

      <div class="app-publish__summary">
        <strong>${escapeHtml(detail.locationLabel)}</strong>
        <span>${escapeHtml(categoryLabel)}</span>
        <em>${escapeHtml(formatListingPrice(detail))}</em>
      </div>

      ${detail.publishedAt && Number.isFinite(Date.parse(detail.publishedAt)) ? `<p>Annonce publiée le ${escapeHtml(new Date(detail.publishedAt).toLocaleDateString('fr-FR'))}</p>` : ''}
      ${renderDetailMedia(detail, selectedImageIndex)}

      <div class="app-auth__card">
        <strong>Description</strong>
        <p>${escapeHtml(detail.summary)}</p>
      </div>

      ${renderAttributeDetails(detail)}

      ${renderSellerBlock(detail.seller)}
      ${renderSafetyCard(detail)}
      ${
        detail.viewerRole === 'owner'
          ? renderOwnerLifecycleCard(detail)
          : `
            ${contactError ? `<p class="app-flow__error" role="alert">${escapeHtml(contactError)}</p><a href="#messages">Mes messages</a>` : ''}
            <div class="app-flow__actions" data-contact-actions="${escapeAttribute(detail.contactActions.join(','))}">
              ${['whatsapp', 'message', 'call']
                .filter((action) =>
                  action === 'message'
                    ? Boolean(detail.id)
                    : detail.contactActions.includes(action),
                )
                .map((action) => buildActionMarkup(action, detail, contactBusy))
                .join('')}

            </div>
          `
      }

      ${renderReviewForm(detail)}
    </section>
  `;
}
